import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, Mail, Lock, User, AlertCircle, Loader2, CheckCircle, Plus, Users } from 'lucide-react';

export const Register = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    company_name: '',
  });
  const [companyCode, setCompanyCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Register | ERP System';
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setError('');
    setSuccess('');
  };

  // Create New Company
  const handleCreateCompanySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await authAPI.register({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
      });
      setSuccess('Company created successfully! You can now login.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Join Existing Company
  const handleJoinCompanySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await authAPI.joinCompany({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        company_name: ''
      }, companyCode);
      setSuccess(response.data.message || 'Request sent! Wait for admin approval.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join company. Please check the company code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-4 text-center pb-4">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Get Started
              </CardTitle>
              <CardDescription className="text-slate-500 mt-2">
                Create a new company or join an existing one
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetForm(); }} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="create" className="flex items-center gap-2" data-testid="tab-create-company">
                  <Plus className="w-4 h-4" />
                  New Company
                </TabsTrigger>
                <TabsTrigger value="join" className="flex items-center gap-2" data-testid="tab-join-company">
                  <Users className="w-4 h-4" />
                  Join Company
                </TabsTrigger>
              </TabsList>

              {/* Create New Company Tab */}
              <TabsContent value="create">
                <form onSubmit={handleCreateCompanySubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">{success}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="company_name"
                        name="company_name"
                        type="text"
                        placeholder="Your Company (Pvt) Ltd"
                        value={formData.company_name}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        data-testid="company-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="full_name">Your Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="full_name"
                        name="full_name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.full_name}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        data-testid="fullname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        minLength={6}
                        data-testid="password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium mt-2"
                    disabled={loading || success}
                    data-testid="create-company-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Company...
                      </>
                    ) : success ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Company Created
                      </>
                    ) : (
                      'Create Company'
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Join Existing Company Tab */}
              <TabsContent value="join">
                <form onSubmit={handleJoinCompanySubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">{success}</AlertDescription>
                    </Alert>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                    Ask your company admin for the Company Code to join.
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="join_full_name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="join_full_name"
                        name="full_name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.full_name}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        data-testid="join-fullname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="join_email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="join_email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        data-testid="join-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="join_password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="join_password"
                        name="password"
                        type="password"
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={handleChange}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                        required
                        minLength={6}
                        data-testid="join-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_code">Company Code</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="company_code"
                        type="text"
                        placeholder="Enter company code from admin"
                        value={companyCode}
                        onChange={(e) => setCompanyCode(e.target.value)}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white font-mono"
                        required
                        data-testid="company-code-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium mt-2"
                    disabled={loading || success}
                    data-testid="join-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Requesting Access...
                      </>
                    ) : success ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Request Sent
                      </>
                    ) : (
                      'Request to Join'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="login-link">
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-400 mt-6">
          ERP System · Cloud Business Management
        </p>
      </div>
    </div>
  );
};

export default Register;
